import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Copy, Search, FileText, Wrench, Zap, Wind, Hammer, AlertTriangle, Settings, DollarSign, Clock
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { quoteTemplateService } from '@/services/quoteTemplateService';
import { QUOTE_CATEGORIES, type QuoteTemplate } from '@/types/quoteTemplate';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';

export function QuoteTemplatesPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<QuoteTemplate[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalTemplates: 0,
    totalUsage: 0,
    averageSuccessRate: 0,
    topPerforming: null as QuoteTemplate | null
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    basePrice: '',
    maxPrice: '',
    timeEstimate: '',
    materialsIncluded: false,
    warranty: '',
    notes: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTemplates();
    loadStats();
  }, [user]);

  useEffect(() => {
    filterTemplates();
  }, [templates, selectedCategory, searchTerm]);

  const loadTemplates = async () => {
    if (!user) return;
    try {
      const userTemplates = await quoteTemplateService.getTemplatesByProvider(user.id);
      setTemplates(userTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadStats = async () => {
    if (!user) return;
    try {
      const templateStats = await quoteTemplateService.getTemplateStats(user.id);
      setStats(templateStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template => template.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleDeleteTemplate = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await quoteTemplateService.deleteTemplate(id);
        await loadTemplates();
        await loadStats();
      } catch (error) {
        console.error('Failed to delete template:', error);
      }
    }
  };

  const handleDuplicateTemplate = async (template: QuoteTemplate) => {
    try {
      const duplicatedTemplate = {
        ...template,
        name: `${template.name} (Copy)`,
        providerId: user!.id
      };
      // Type assertion needed due to omitted properties in create logic
      delete (duplicatedTemplate as any).id;
      delete (duplicatedTemplate as any).usageCount;
      delete (duplicatedTemplate as any).successRate;
      delete (duplicatedTemplate as any).createdAt;
      delete (duplicatedTemplate as any).updatedAt;

      await quoteTemplateService.createTemplate(duplicatedTemplate);
      await loadTemplates();
      await loadStats();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    }
  };

  const handleCreateTemplate = async () => {
    if (!user || !formData.name || !formData.category || !formData.basePrice) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await quoteTemplateService.createTemplate({
        providerId: user.id,
        name: formData.name,
        category: formData.category,
        description: formData.description,
        basePrice: parseFloat(formData.basePrice),
        maxPrice: formData.maxPrice ? parseFloat(formData.maxPrice) : parseFloat(formData.basePrice),
        timeEstimate: formData.timeEstimate,
        materialsIncluded: formData.materialsIncluded,
        warranty: formData.warranty,
        notes: formData.notes,
        isActive: true
      });

      // Reset form
      setFormData({
        name: '',
        category: '',
        description: '',
        basePrice: '',
        maxPrice: '',
        timeEstimate: '',
        materialsIncluded: false,
        warranty: '',
        notes: ''
      });

      setShowCreateModal(false);
      await loadTemplates();
      await loadStats();
    } catch (error) {
      console.error('Failed to create template:', error);
      alert('Failed to create template');
    }
  };

  const getCategoryIcon = (categoryId: string) => {
    const iconMap: Record<string, any> = {
      plumbing: Wrench,
      electrical: Zap,
      hvac: Wind,
      general: Hammer,
      emergency: AlertTriangle,
      custom: Settings
    };
    return iconMap[categoryId] || FileText;
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/provider/dashboard' }, { label: 'Quote Templates' }]} />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Quote Templates</h1>
            <p className="text-gray-600">Manage and reuse templates to speed up your quoting process.</p>
          </div>
          <Button icon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateModal(true)}>
            Create Template
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Templates</div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalTemplates}</div>
          </Card>
          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Usage</div>
            <div className="text-3xl font-bold text-blue-600">{stats.totalUsage}</div>
          </Card>
          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Avg Success Rate</div>
            <div className="text-3xl font-bold text-emerald-600">{stats.averageSuccessRate}%</div>
          </Card>
          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Top Performer</div>
            <Tooltip content={stats.topPerforming?.name || 'None'}>
              <div className="text-lg font-bold text-purple-600 truncate cursor-help">
                {stats.topPerforming ? stats.topPerforming.name : '—'}
              </div>
            </Tooltip>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${selectedCategory === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              All
            </button>
            {QUOTE_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${selectedCategory === category.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {category.name}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-80">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full form-input pl-10 rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => {
            const CategoryIcon = getCategoryIcon(template.category);
            const categoryInfo = QUOTE_CATEGORIES.find(c => c.id === template.category);

            return (
              <Card key={template.id} className="flex flex-col h-full hover:shadow-md transition-shadow duration-200 border-gray-200">
                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-50 rounded-lg text-primary-600">
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 line-clamp-1" title={template.name}>{template.name}</h3>
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">{categoryInfo?.name}</div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 min-h-[3em]">
                    {template.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-2"><DollarSign className="w-3 h-3" /> Price Range</span>
                      <span className="font-medium text-gray-900">€{template.basePrice} {template.maxPrice && template.maxPrice !== template.basePrice && `- €${template.maxPrice}`}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-2"><Clock className="w-3 h-3" /> Duration</span>
                      <span className="font-medium text-gray-900">{template.timeEstimate}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant={template.materialsIncluded ? 'success' : 'neutral'}>
                      {template.materialsIncluded ? 'Materials Included' : 'Labor Only'}
                    </Badge>
                    <Badge variant={template.isActive ? 'success' : 'neutral'}>
                      {template.isActive ? 'Active' : 'Archived'}
                    </Badge>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between rounded-b-lg">
                  <div className="text-xs text-gray-500 font-medium">
                    Used {template.usageCount} times • {template.successRate}% Success
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleDuplicateTemplate(template)} title="Duplicate">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/quote-templates/${template.id}/edit`)} title="Edit">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(template.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-20 bg-white rounded-lg border border-dashed border-gray-300">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No templates found</h3>
            <p className="text-gray-500 mb-6">Create your first quote template to get started.</p>
            <Button onClick={() => setShowCreateModal(true)}>Create Template</Button>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl p-0 overflow-hidden max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">Create New Template</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                  <span className="sr-only">Close</span>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-6 space-y-4 overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <input 
                    type="text" 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                    placeholder="e.g. Standard Plumbing Repair" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select 
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full form-select rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Select a category</option>
                    {QUOTE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full form-textarea rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                    rows={3} 
                    placeholder="Describe the service..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (€) *</label>
                    <input 
                      type="number" 
                      value={formData.basePrice}
                      onChange={(e) => setFormData({...formData, basePrice: e.target.value})}
                      className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                      placeholder="0.00" 
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Price (€)</label>
                    <input 
                      type="number" 
                      value={formData.maxPrice}
                      onChange={(e) => setFormData({...formData, maxPrice: e.target.value})}
                      className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                      placeholder="Optional" 
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Estimate</label>
                  <input 
                    type="text" 
                    value={formData.timeEstimate}
                    onChange={(e) => setFormData({...formData, timeEstimate: e.target.value})}
                    className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                    placeholder="e.g. 2-3 hours" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Period</label>
                  <input 
                    type="text" 
                    value={formData.warranty}
                    onChange={(e) => setFormData({...formData, warranty: e.target.value})}
                    className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                    placeholder="e.g. 6 months parts & labor" 
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.materialsIncluded}
                      onChange={(e) => setFormData({...formData, materialsIncluded: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                    />
                    <span className="text-sm font-medium text-gray-700">Materials Included</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                  <textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full form-textarea rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
                    rows={2} 
                    placeholder="Any additional information..."
                  />
                </div>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button onClick={handleCreateTemplate}>Create Template</Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}